import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { FolderUpIcon } from '@/shared/assets/icons/folder-up-icon';
import { DrawerController } from '@/shared/components/ui/drawer-controller/drawer-controller';
import { Form } from '@/shared/components/ui/form/form';
import { TSubmittedData } from '@/shared/components/ui/form/form.types';
import { Trigger } from '@/shared/components/ui/trigger/trigger';
import { AddResourcesToGroupForm } from '@/shared/features/groups/components/add-resources-to-group/add-resources-to-group-form';
import {
  addResourcesToGroupInputSchema,
  TAddResourcesToGroupInputSchema,
} from '@/shared/features/groups/components/add-resources-to-group/add-resources-to-group.validation';
import { TMoveResourcesProps } from '@/shared/features/groups/components/move-resources/move-resources.types';
import {
  getInitialGroupId,
  isRootGroup,
} from '@/shared/features/groups/components/move-resources/move-resources.utils';
import { useGroupsConfig } from '@/shared/features/groups/context/groups-config-context';
import { useGroupResourceActions } from '@/shared/features/groups/hooks/use-group-resource-actions/use-group-resource-actions';
import { Can } from '@/shared/features/permissions/components/can/can';
import { useReadAllResourceGroupsQuery } from '@/shared/redux/api/assets/groups/groups-api';

export const MoveResources: React.FC<TMoveResourcesProps> = ({
  resources,
  component = 'MenuItem',
  onClose,
  onSuccess,
  permission,
}) => {
  const { t } = useTranslation();
  const { groupId } = useParams();
  const { permissionActions } = useGroupsConfig();
  const { handleAddResourcesToGroup } = useGroupResourceActions();
  const { data: allGroupsData } = useReadAllResourceGroupsQuery();

  const rootGroupResources = resources.filter(isRootGroup);
  const rootGroupIds: string[] | undefined =
    rootGroupResources.length > 0
      ? rootGroupResources.map((resource) => resource.groupId)
      : undefined;

  const rootGroupsCount =
    allGroupsData?.readAllResourceGroups.items.filter(
      (group) => !group.parentGroupId,
    ).length ?? 0;

  // Moving every root group at once would leave the tree without one.
  const isDisabled = !!rootGroupIds && rootGroupIds.length === rootGroupsCount;

  const onSubmit = async (
    data: TSubmittedData<TAddResourcesToGroupInputSchema>,
  ) => {
    await handleAddResourcesToGroup(data, resources, onSuccess, onClose);
  };

  return (
    <Can action={permissionActions.updateGroup}>
      <DrawerController
        trigger={
          <Trigger
            icon={<FolderUpIcon />}
            component={component}
            label={t('common:move')}
            buttonVariant="secondary"
            disabled={isDisabled}
            tooltip={
              isDisabled ? t('groups:cannot_move_all_root_groups') : undefined
            }
          />
        }
        title={t('groups:move_to_group')}
        permission={permission}
        disabled={isDisabled}
      >
        {(handleClose) => (
          <Form<TAddResourcesToGroupInputSchema>
            onSubmit={onSubmit}
            schema={addResourcesToGroupInputSchema}
            defaultValues={{
              groupId: getInitialGroupId(resources, groupId),
            }}
          >
            {() => (
              <AddResourcesToGroupForm
                resources={resources}
                handleClose={handleClose}
                submitButtonLabel={t('common:move')}
              />
            )}
          </Form>
        )}
      </DrawerController>
    </Can>
  );
};
